import { emptyRollLog } from "../session/log";
import type { Character, ChoiceSource, Level, LevelNumber } from "./model";
import { CHARACTER_SCHEMA, DEFAULT_VARIANT_RULES, emptyInventory, emptySpellcasting, isLevelNumber } from "./model";

export function newId(): string {
  return crypto.randomUUID();
}

export function now(): string {
  return new Date().toISOString();
}

/** The four foundations every Character opens at Level 1. Later Levels come from the guided level-up (#9). */
const FOUNDATIONS: ChoiceSource[] = [{ kind: "ancestry" }, { kind: "heritage" }, { kind: "background" }, { kind: "class" }];

export function emptyLevel(number: LevelNumber): Level {
  return { number, choices: number === 1 ? FOUNDATIONS.map((source) => ({ source })) : [] };
}

export function createCharacter(name: string, snapshotTag: string): Character {
  const at = now();
  return {
    schema: CHARACTER_SCHEMA,
    id: newId(),
    name: name.trim() || "Unnamed",
    createdAt: at,
    updatedAt: at,
    snapshotTag,
    level: 1,
    levels: [emptyLevel(1)],
    variantRules: { ...DEFAULT_VARIANT_RULES },
    overrides: [],
    iwr: [],
    toggles: {},
    notes: "",
    spellcasting: emptySpellcasting(),
    inventory: emptyInventory(),
    session: null,
    rollLog: emptyRollLog(),
    familiar: null,
    migrations: [],
  };
}

/** A deep copy with a new id, a new name and fresh timestamps. The Session is not copied. */
export function duplicateCharacter(source: Character, name = `${source.name} (copy)`): Character {
  const at = now();
  return { ...structuredClone(source), id: newId(), name, createdAt: at, updatedAt: at, session: null, rollLog: emptyRollLog() };
}

export function renameCharacter(character: Character, name: string): Character {
  return { ...character, name: name.trim() || character.name };
}

/** Make Levels 1..n exist. Levels above are left alone (they hold planned-ahead Choices). */
export function ensureLevels(character: Character, upTo: number): Character {
  if (!isLevelNumber(upTo)) throw new RangeError(`Level ${upTo} is outside 1 to 20`);
  const levels = [...character.levels];
  for (let n = levels.length + 1; n <= upTo; n++) levels.push(emptyLevel(n as LevelNumber));
  return { ...character, levels };
}
